import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { GlassCard, GlassButton } from '../../components/ui/GlassComponents';
import { FiCreditCard, FiTrendingUp, FiDollarSign, FiCalendar, FiSearch, FiFilter } from 'react-icons/fi';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { format, parseISO, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const AdminPayments = () => {
  // Payments table state
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Analytics state
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [timeframe, setTimeframe] = useState('month');
  const [period, setPeriod] = useState(12);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: subMonths(startOfMonth(new Date()), 12),
    endDate: endOfMonth(new Date())
  });
  
  // View state
  const [activeTab, setActiveTab] = useState('analytics'); // 'analytics' or 'payments'

  useEffect(() => {
    if (activeTab === 'payments') {
      fetchPayments();
    }
  }, [currentPage, activeTab, searchTerm, statusFilter, planFilter, dateRange]);
  
  useEffect(() => {
    if (activeTab === 'analytics') {
      fetchAnalytics();
    }
  }, [activeTab, timeframe, period]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/payments', {
        params: { 
          page: currentPage,
          search: searchTerm,
          status: statusFilter,
          plan: planFilter,
          startDate: dateRange.startDate ? dateRange.startDate.toISOString() : null,
          endDate: dateRange.endDate ? dateRange.endDate.toISOString() : null
        }
      });
      setPayments(response.data.payments);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const response = await axios.get('/api/admin/payments/analytics', {
        params: { 
          timeframe,
          period
        }
      });
      setAnalytics(response.data.analytics);
    } catch (error) {
      console.error('Error fetching payment analytics:', error);
      toast.error('Failed to load payment analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  
  const handleTimeframeChange = (e) => {
    setTimeframe(e.target.value);
  };
  
  const handlePeriodChange = (e) => {
    setPeriod(parseInt(e.target.value));
  };
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };
  
  const handlePlanFilterChange = (e) => {
    setPlanFilter(e.target.value);
  };
  
  const handleDateRangeChange = (ranges) => {
    setDateRange({
      startDate: ranges.selection.startDate,
      endDate: ranges.selection.endDate
    });
  };
  
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  const toggleDatePicker = () => {
    setShowDatePicker(!showDatePicker);
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return format(date, 'MMM d, yyyy');
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'succeeded':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };
  
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];
  
  if ((loading && payments.length === 0 && activeTab === 'payments') || 
      (analyticsLoading && !analytics && activeTab === 'analytics')) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <GlassCard className="p-8">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <span className="text-gray-600 dark:text-gray-400">Loading payment data...</span>
          </div>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
            Payment Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Monitor payment analytics and transaction history</p>
        </div>
        
        {/* Tab Navigation */}
        <GlassCard className="mb-6 p-1">
          <div className="flex rounded-xl bg-gray-100/50 dark:bg-gray-800/50 p-1">
            <button
              className={`flex-1 py-3 px-6 font-medium text-sm rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 ${
                activeTab === 'analytics' 
                  ? 'bg-white/80 dark:bg-gray-700/80 text-blue-600 dark:text-blue-400 shadow-sm' 
                  : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-white/40 dark:hover:bg-gray-700/40'
              }`}
              onClick={() => handleTabChange('analytics')}
            >
              <FiTrendingUp className="h-4 w-4" />
              <span>Analytics & Insights</span>
            </button>
            <button
              className={`flex-1 py-3 px-6 font-medium text-sm rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 ${
                activeTab === 'payments' 
                  ? 'bg-white/80 dark:bg-gray-700/80 text-blue-600 dark:text-blue-400 shadow-sm' 
                  : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-white/40 dark:hover:bg-gray-700/40'
              }`}
              onClick={() => handleTabChange('payments')}
            >
              <FiCreditCard className="h-4 w-4" />
              <span>Payment History</span>
            </button>
          </div>
        </GlassCard>
      
      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-8">
          {/* Controls */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Timeframe</label>
              <select 
                value={timeframe}
                onChange={handleTimeframeChange}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="day">Daily</option>
                <option value="week">Weekly</option>
                <option value="month">Monthly</option>
                <option value="year">Yearly</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Period</label>
              <select 
                value={period}
                onChange={handlePeriodChange}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="3">Last 3 {timeframe}s</option>
                <option value="6">Last 6 {timeframe}s</option>
                <option value="12">Last 12 {timeframe}s</option>
                <option value="24">Last 24 {timeframe}s</option>
              </select>
            </div>
          </div>
          
          {/* Summary Cards */}
          {analytics && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* MRR Card */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Monthly Recurring Revenue</h3>
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 mt-2">
                  {formatCurrency(analytics.mrr.total)}
                </p>
                <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  From {analytics.mrr.byPlan.length} active subscriptions
                </div>
              </div>
              
              {/* Churn Rate Card */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Churn Rate</h3>
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 mt-2">
                  {analytics.churnRate.toFixed(1)}%
                </p>
                <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Based on last {period} {timeframe}s
                </div>
              </div>
              
              {/* Payment Success Rate Card */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Payment Success Rate</h3>
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 mt-2">
                  {analytics.paymentSuccess.rate.toFixed(1)}%
                </p>
                <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {analytics.paymentSuccess.successful} successful / {analytics.paymentSuccess.total} total
                </div>
              </div>
              
              {/* Total Revenue Card */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Total Revenue</h3>
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 mt-2">
                  {formatCurrency(analytics.revenueByPeriod.reduce((sum, item) => sum + parseFloat(item.revenue), 0))}
                </p>
                <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Last {period} {timeframe}s
                </div>
              </div>
            </div>
          )}
          
          {/* Charts */}
          {analytics && (
            <div className="space-y-8">
              {/* Revenue Over Time Chart */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Revenue Over Time</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={analytics.revenueByPeriod.map(item => ({
                        period: format(new Date(item.period), timeframe === 'month' ? 'MMM yyyy' : 
                                     timeframe === 'week' ? 'wo yyyy' : 
                                     timeframe === 'day' ? 'dd MMM' : 'yyyy'),
                        revenue: parseFloat(item.revenue),
                        count: parseInt(item.count)
                      }))}
                      margin={{ top: 5, right: 30, left: 20, bottom: 25 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis 
                        dataKey="period" 
                        tick={{ fill: '#9CA3AF' }}
                        angle={-45}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis 
                        yAxisId="left"
                        tick={{ fill: '#9CA3AF' }}
                        tickFormatter={(value) => `£${value}`}
                      />
                      <YAxis 
                        yAxisId="right" 
                        orientation="right" 
                        tick={{ fill: '#9CA3AF' }}
                      />
                      <Tooltip 
                        formatter={(value, name) => [
                          name === 'revenue' ? formatCurrency(value) : value,
                          name === 'revenue' ? 'Revenue' : 'Transactions'
                        ]}
                        labelFormatter={(label) => `Period: ${label}`}
                      />
                      <Legend />
                      <Line 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="revenue" 
                        name="Revenue" 
                        stroke="#3B82F6" 
                        activeDot={{ r: 8 }} 
                        strokeWidth={2}
                      />
                      <Line 
                        yAxisId="right"
                        type="monotone" 
                        dataKey="count" 
                        name="Transactions" 
                        stroke="#10B981" 
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              
              {/* Revenue by Plan Chart */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Revenue by Plan</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={analytics.revenueByPlan.map(item => ({
                          plan: item.plan || 'Unknown',
                          revenue: parseFloat(item.revenue),
                          count: parseInt(item.count)
                        }))}
                        margin={{ top: 5, right: 30, left: 20, bottom: 25 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="plan" tick={{ fill: '#9CA3AF' }} />
                        <YAxis tick={{ fill: '#9CA3AF' }} tickFormatter={(value) => `£${value}`} />
                        <Tooltip 
                          formatter={(value, name) => [
                            name === 'revenue' ? formatCurrency(value) : value,
                            name === 'revenue' ? 'Revenue' : 'Transactions'
                          ]}
                        />
                        <Legend />
                        <Bar dataKey="revenue" name="Revenue" fill="#3B82F6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={analytics.revenueByPlan.map((item, index) => ({
                            name: item.plan || 'Unknown',
                            value: parseFloat(item.revenue)
                          }))}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          nameKey="name"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {analytics.revenueByPlan.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
              
              {/* MRR by Plan Chart */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Monthly Recurring Revenue by Plan</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={analytics.mrr.byPlan}
                        margin={{ top: 5, right: 30, left: 20, bottom: 25 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="plan" tick={{ fill: '#9CA3AF' }} />
                        <YAxis tick={{ fill: '#9CA3AF' }} tickFormatter={(value) => `£${value}`} />
                        <Tooltip 
                          formatter={(value, name) => [
                            name === 'mrr' ? formatCurrency(value) : value,
                            name === 'mrr' ? 'MRR' : 'Subscriptions'
                          ]}
                        />
                        <Legend />
                        <Bar dataKey="mrr" name="MRR" fill="#8B5CF6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={analytics.mrr.byPlan.map((item, index) => ({
                            name: item.plan,
                            value: item.count
                          }))}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          nameKey="name"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {analytics.mrr.byPlan.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => `${value} subscriptions`} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Payments History Tab */}
      {activeTab === 'payments' && (
        <div>
          {/* Filters */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by team name"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="">All Statuses</option>
                <option value="succeeded">Succeeded</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Plan</label>
              <select
                value={planFilter}
                onChange={handlePlanFilterChange}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="">All Plans</option>
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date Range</label>
              <div className="relative">
                <button
                  onClick={toggleDatePicker}
                  className="mt-1 flex justify-between items-center w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <span>
                    {dateRange.startDate ? format(dateRange.startDate, 'dd/MM/yyyy') : 'Start Date'} - {dateRange.endDate ? format(dateRange.endDate, 'dd/MM/yyyy') : 'End Date'}
                  </span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                {showDatePicker && (
                  <div className="absolute z-10 mt-1 w-auto bg-white dark:bg-gray-800 shadow-lg rounded-md">
                    <DateRangePicker
                      ranges={[{
                        startDate: dateRange.startDate,
                        endDate: dateRange.endDate,
                        key: 'selection'
                      }]}
                      onChange={handleDateRangeChange}
                      staticRanges={[
                        {
                          label: 'Last 30 Days',
                          range: () => ({
                            startDate: subMonths(new Date(), 1),
                            endDate: new Date()
                          }),
                          isSelected: () => false
                        },
                        {
                          label: 'This Month',
                          range: () => ({
                            startDate: startOfMonth(new Date()),
                            endDate: new Date()
                          }),
                          isSelected: () => false
                        },
                        {
                          label: 'Last Month',
                          range: () => ({
                            startDate: startOfMonth(subMonths(new Date(), 1)),
                            endDate: endOfMonth(subMonths(new Date(), 1))
                          }),
                          isSelected: () => false
                        },
                        {
                          label: 'Last 3 Months',
                          range: () => ({
                            startDate: subMonths(new Date(), 3),
                            endDate: new Date()
                          }),
                          isSelected: () => false
                        },
                        {
                          label: 'Last 6 Months',
                          range: () => ({
                            startDate: subMonths(new Date(), 6),
                            endDate: new Date()
                          }),
                          isSelected: () => false
                        },
                        {
                          label: 'Last 12 Months',
                          range: () => ({
                            startDate: subMonths(new Date(), 12),
                            endDate: new Date()
                          }),
                          isSelected: () => false
                        }
                      ]}
                    />
                    <div className="p-2 flex justify-end border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={toggleDatePicker}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm font-medium"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="md:col-span-2 flex items-end">
              <button
                onClick={fetchPayments}
                className="w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-700 dark:hover:bg-blue-800"
              >
                Apply Filters
              </button>
            </div>
          </div>
          
          {/* Payments List */}
          <div className="bg-white dark:bg-gray-800 shadow overflow-hidden rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Team
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Amount
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Plan
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {payments.length > 0 ? (
                  payments.map((payment) => (
                    <tr key={payment.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {payment.Team ? payment.Team.name : 'Unknown Team'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {formatCurrency(payment.amount)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {formatDate(payment.paymentDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                          {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {payment.plan ? payment.plan.charAt(0).toUpperCase() + payment.plan.slice(1) : 'N/A'}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                      No payments found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default AdminPayments;
